import { userCacheKey } from '../quiz-generation/utils/paperProgressCache';

/**
 * A written mock interview paper, on this device. While it's being sat: the
 * run each started question is marked in (the server holds every answer
 * already submitted, so a reload rebuilds the paper from those runs), the
 * unsent text in each answer box, and the time spent. Once finished: the run
 * ids its review and feedback are read from. One entry of each per
 * (user, paper, session), under the signed-in user's id (userCacheKey): no user
 * id, nothing is read or written. Storage failures degrade to "nothing saved",
 * never a crash. Logout clears it for every user (interviewProgressStorage).
 */
export const DRAFT_PREFIX = 'ivori-written-draft:';
export const RESULT_PREFIX = 'ivori-written-result:';

const keyOf = (prefix, userId, paperId, sessionId) => userCacheKey(`${prefix}${paperId}:${sessionId}`, userId);

function read(key) {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function write(key, value) {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* private window or quota: the paper still works */ }
}

function remove(key) {
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch { /* nothing to clean up */ }
}

/** { runs: {questionId: runId}, drafts: {questionId: text}, elapsed } */
export const loadDraft = (userId, paperId, sessionId) => read(keyOf(DRAFT_PREFIX, userId, paperId, sessionId));
export const saveDraft = (userId, paperId, sessionId, draft) => write(keyOf(DRAFT_PREFIX, userId, paperId, sessionId), draft);
export const clearDraft = (userId, paperId, sessionId) => remove(keyOf(DRAFT_PREFIX, userId, paperId, sessionId));

export const loadResult = (userId, paperId, sessionId) => read(keyOf(RESULT_PREFIX, userId, paperId, sessionId));
export const saveResult = (userId, paperId, sessionId, runIds) => write(
  keyOf(RESULT_PREFIX, userId, paperId, sessionId), { runIds },
);
export const clearResult = (userId, paperId, sessionId) => remove(keyOf(RESULT_PREFIX, userId, paperId, sessionId));

/** 'none' | 'draft' | 'marked' */
export function writtenProgress(userId, paperId, sessionId) {
  if (loadResult(userId, paperId, sessionId)?.runIds?.length) return 'marked';
  const draft = loadDraft(userId, paperId, sessionId);
  const started = Object.values(draft?.runs || {}).some(Boolean);
  const typed = Object.values(draft?.drafts || {}).some((t) => (t || '').trim());
  return started || typed ? 'draft' : 'none';
}
